import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { X as XIcon } from 'lucide-react';
import { WMHD_PATH, isWmhdSeason, wmhdNudgeLine } from '../content/worldMentalHealthDay.meta';

// A quiet toast pointing to the World Mental Health Day page. October only,
// once per visit for each place, after a short wait, and it hides itself.
// inline: sits in the flow of its parent (for example, stacked above a button) instead of floating,
// so it can never cover what is next to it.
export default function WmhdNudge({ isNight, place, delay = 8000, hidden = false, bottom = 'bottom-28', inline = false }: { isNight: boolean; place: string; delay?: number; hidden?: boolean; bottom?: string; inline?: boolean }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!isWmhdSeason()) return;
    const key = `zuzu_wmhd_nudge_${place}`;
    try { if (sessionStorage.getItem(key)) return; } catch { /* private mode: still fine */ }
    const open = window.setTimeout(() => {
      setShow(true);
      try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
    }, delay);
    const close = window.setTimeout(() => setShow(false), delay + 15000);
    return () => { clearTimeout(open); clearTimeout(close); };
  }, [place, delay]);

  return (
    <AnimatePresence>
      {show && !hidden && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} role="status"
          className={`${inline ? 'w-full' : `fixed ${bottom} left-1/2 -translate-x-1/2 z-[85] w-[min(24rem,calc(100%-2rem))]`} flex items-center gap-3 pl-4 pr-2 py-2 rounded-full text-sm backdrop-blur-md border ${
            isNight ? 'bg-black/50 border-white/10 text-white' : 'bg-white/80 border-black/10 text-black'
          }`}>
          <span className="flex-1 min-w-0 opacity-85 truncate text-[0.8125rem]">
            {/* The date only where there is room; phones keep it short */}
            {wmhdNudgeLine().replace(', 10 Oct', '')}{wmhdNudgeLine().endsWith(', 10 Oct') && <span className="hidden sm:inline">, 10 Oct</span>}
          </span>
          <Link to={WMHD_PATH} onClick={() => setShow(false)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}>
            Resources
          </Link>
          <button type="button" onClick={() => setShow(false)} aria-label="Close" className="shrink-0 p-1.5 opacity-50 hover:opacity-100"><XIcon size={14} /></button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
